import React, { useState } from "react";
import axios from "axios";
import DynamicPage from "../../DynamicPage";
import ImageUploader from "./ImageUploader";
import ImageSelector from "./ImageSelector";

const PageComposer = ({ existingPage = null, onSave, onCancel }) => {
    const [metadata, setMetadata] = useState({
        title: existingPage?.title || "",
        slug: existingPage?.slug || "",
        isMainPage: existingPage?.isMainPage || false,
    });

    const [sections, setSections] = useState(existingPage?.sections || []);
    const [expandedSections, setExpandedSections] = useState([]);
    const [imagePath, setImagePath] = useState("");
    const [draggedIndex, setDraggedIndex] = useState(null);

    const handleDragStart = (index) => {
        setDraggedIndex(index);
    };

    const handleDragEnter = (index) => {
        if (draggedIndex === null || draggedIndex === index) return;

        const updatedSections = [...sections];
        const [movedSection] = updatedSections.splice(draggedIndex, 1);
        updatedSections.splice(index, 0, movedSection);

        setSections(updatedSections);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    // Add Section with Templates
    const addSection = (type) => {
        const templates = {
            text: { type, content: "", className: "text-lg" },
            image: { type, src: "", alt: "", className: "rounded-lg", width: 500, height: 300 },
            quote: { type, content: "Inspirational quote here.", className: "italic text-center" },
            "horizontal-rule": { type, className: "my-4 border-b" },
            "line-break": { type },
            section: {
                type,
                title: "Section Title",
                content: "Section content",
                additional: "",
                className: "section-class",
            },
            list: {
                type,
                listType: "unordered",
                items: ["List item 1", "List item 2"],
                className: "list-disc px-6",
            },
            accordion: {
                type,
                items: [
                    { title: "Accordion Item 1", content: "Content for item 1" },
                    { title: "Accordion Item 2", content: "Content for item 2" },
                ],
                className: "accordion",
            },
            card: {
                type,
                title: "Card Title",
                body: "Card body text",
                image: { src: "", alt: "Card Image", className: "rounded-lg" },
            },
            audio: {
                type,
                title: "Audio Playlist Title",
                playlist: [],
                className: "audio-class",
            },
            upcomingEvents: {
                type,
                title: "Upcoming Events",
                description: "Details about upcoming events",
                eventCount: 5,
            },
            iframe: {
                type,
                src: "https://example.com",
                className: "iframe-class",
                style: { width: "100%", height: "400px" },
            },
            videoPlaylist: {
                type,
                videos: [{ title: "Video 1", url: "https://youtube.com" }],
            },
            blogPosts: { type, className: "blog-posts-class" },
            "paypal-donation": {
                type,
                clientId: "your-client-id",
                currency: "USD",
                amount: "10.00",
            },
            "venmo-button": {
                type,
                username: "solidrock",
                amount: "10.00",
                note: "Donation for Solid Rock Cathedral",
            },
            map: {
                type,
                embedUrl: "https://maps.google.com",
                width: "100%",
                height: "450px",
            },
            "contact-form": { type, className: "contact-form-class" },
        };
        setSections([...sections, templates[type]]);
        setExpandedSections([...expandedSections, sections.length]); // Expand new section
    };

    // Update Section
    const updateSection = (index, updatedSection) => {
        const updatedSections = [...sections];
        updatedSections[index] = updatedSection;
        setSections(updatedSections);
    };

    // Remove Section
    const removeSection = (index) => {
        setSections(sections.filter((_, i) => i !== index));
        setExpandedSections(expandedSections.filter((i) => i !== index));
    };

    // Toggle Expand/Collapse
    const toggleSection = (index) => {
        if (expandedSections.includes(index)) {
            setExpandedSections(expandedSections.filter((i) => i !== index));
        } else {
            setExpandedSections([...expandedSections, index]);
        }
    };

    // Handle Save
    const handleSave = async () => {
        const pageData = {
            page: metadata.page || metadata.title, // Default to title if page name is not explicitly provided
            slug: metadata.slug,
            title: metadata.title,
            sections,
            isMainPage: metadata.isMainPage || false,
        };

        try {
            const token = localStorage.getItem("token");
            if (existingPage?._id) {
                await axios.put(`/api/pages/${existingPage._id}`, pageData, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                await axios.post(`/api/pages`, pageData, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }

            onSave(); // Callback to refresh the list
        } catch (error) {
            console.error("Error saving page:", error);
            alert(error.response?.data?.error || "Failed to save page.");
        }
    };

    const applyFormatting = (index, format, value) => {
        const section = sections[index];
        if (!section || !section.content) return;

        const textarea = document.querySelector(`textarea[data-index="${index}"]`);
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = textarea.value.substring(start, end);

        if (!selectedText) {
            alert("Please select text to format.");
            return;
        }

        let formattedText = selectedText;

        switch (format) {
            case "bold":
                formattedText = `<strong>${selectedText}</strong>`;
                break;
            case "italic":
                formattedText = `<em>${selectedText}</em>`;
                break;
            case "underline":
                formattedText = `<u>${selectedText}</u>`;
                break;
            case "color":
                formattedText = `<span style="color:${value || "red"}">${selectedText}</span>`;
                break;
            case "header":
                formattedText = `<${value}>${selectedText}</${value}>`;
                break;
            default:
                break;
        }

        const updatedContent =
            section.content.substring(0, start) +
            formattedText +
            section.content.substring(end);

        updateSection(index, { ...section, content: updatedContent });


        setTimeout(() => {
            textarea.setSelectionRange(start, start + formattedText.length);
            textarea.focus();
        }, 0);
    };


    const applyWYSIWYGFormatting = (index, command, value = null) => {
        const section = sections[index];
        if (!section || !section.content) return;

        const textarea = document.querySelector(`textarea[data-index="${index}"]`);
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = textarea.value.substring(start, end);

        let formattedText = selectedText;

        switch (command) {
            case "bold":
                formattedText = `<strong>${selectedText}</strong>`;
                break;
            case "italic":
                formattedText = `<em>${selectedText}</em>`;
                break;
            case "underline":
                formattedText = `<u>${selectedText}</u>`;
                break;
            case "foreColor":
                formattedText = `<span style="color:${value || "red"}">${selectedText}</span>`;
                break;
            case "formatBlock":
                formattedText = `<${value}>${selectedText}</${value}>`;
                break;
            default:
                break;
        }

        const updatedContent =
            section.content.substring(0, start) +
            formattedText +
            section.content.substring(end);

        updateSection(index, { ...section, content: updatedContent });


        setTimeout(() => {
            textarea.setSelectionRange(start, start + formattedText.length);
            textarea.focus();
        }, 0);
    };

    const getFormattedContent = (content) => {
        return { __html: content };
    };

    return (
        <div className="page-composer">
            <h2>{existingPage ? "Edit Page" : "Create Page"}</h2>
            <div className="metadata">
                <label>
                    Title:
                    <input
                        type="text"
                        value={metadata.title}
                        onChange={(e) => setMetadata({ ...metadata, title: e.target.value })}
                        className="border rounded px-4 py-2 w-full mb-4"
                    />
                </label>
                <label>
                    Slug:
                    <input
                        type="text"
                        value={metadata.slug}
                        onChange={(e) => setMetadata({ ...metadata, slug: e.target.value })}
                        className="border rounded px-4 py-2 w-full mb-4"
                    />
                </label>
                <label>
                    Main Page:
                    <input
                        type="checkbox"
                        checked={metadata.isMainPage}
                        onChange={(e) => setMetadata({ ...metadata, isMainPage: e.target.checked })}
                        className="ml-2"
                    />
                </label>
            </div>

            <div className="sections">
                <h3>Sections</h3>
                {sections.map((section, index) => (
                    <div
                        key={index}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragEnter={() => handleDragEnter(index)}
                        onDragEnd={handleDragEnd}
                        className="border p-4 rounded mb-4 bg-white shadow hover:bg-gray-50 cursor-move"
                    >
                        <div className="flex justify-between items-center">
                            <h4>Section {index + 1}: {section.type}</h4>
                            <button
                                onClick={() => toggleSection(index)}
                                className="text-blue-500"
                            >
                                {expandedSections.includes(index) ? "Collapse" : "Expand"}
                            </button>
                        </div>
                        {expandedSections.includes(index) && (
                            <div className="mt-4">
                                {section.type === "text" && (
                                    <div className="text-editor">
                                        {/* Toolbar */}
                                        <div className="toolbar mb-2 flex gap-2">
                                            <button
                                                onClick={() => applyWYSIWYGFormatting(index, "bold")}
                                                className="bg-gray-200 px-3 py-1 rounded hover:bg-gray-300"
                                            >
                                                Bold
                                            </button>
                                            <button
                                                onClick={() => applyWYSIWYGFormatting(index, "italic")}
                                                className="bg-gray-200 px-3 py-1 rounded hover:bg-gray-300"
                                            >
                                                Italic
                                            </button>
                                            <button
                                                onClick={() => applyWYSIWYGFormatting(index, "underline")}
                                                className="bg-gray-200 px-3 py-1 rounded hover:bg-gray-300"
                                            >
                                                Underline
                                            </button>
                                            <button
                                                onClick={() => applyWYSIWYGFormatting(index, "foreColor", "red")}
                                                className="bg-gray-200 px-3 py-1 rounded hover:bg-gray-300"
                                            >
                                                Red Text
                                            </button>
                                            <select
                                                onChange={(e) => applyWYSIWYGFormatting(index, "formatBlock", e.target.value)}
                                                className="border rounded px-2 py-1"
                                                defaultValue=""
                                            >
                                                <option value="" disabled>
                                                    Header Level
                                                </option>
                                                <option value="h1">H1</option>
                                                <option value="h2">H2</option>
                                                <option value="h3">H3</option>
                                                <option value="p">Paragraph</option>
                                            </select>
                                        </div>
                                        {/* Textarea Input */}
                                        <textarea
                                            data-index={index}
                                            value={section.content}
                                            onChange={(e) => {
                                                updateSection(index, { ...section, content: e.target.value });
                                            }}
                                            placeholder="Enter your text here"
                                            className="border rounded px-4 py-2 w-full"
                                        />
                                        {/* Preview */}
                                        <div
                                            className="mt-2 border rounded p-2"
                                            dangerouslySetInnerHTML={getFormattedContent(section.content)}
                                        />
                                    </div>
                                )}

                                {section.type === "image" && (
                                    <>
                                        <ImageUploader
                                            onUpload={(path) =>
                                                updateSection(index, { ...section, src: path })
                                            }
                                        />
                                        <ImageSelector
                                            onSelect={(path) =>
                                                updateSection(index, { ...section, src: path })
                                            }
                                        />
                                        <input
                                            type="text"
                                            value={section.alt}
                                            onChange={(e) =>
                                                updateSection(index, { ...section, alt: e.target.value })
                                            }
                                            placeholder="Alt Text"
                                            className="border rounded px-4 py-2 w-full"
                                        />
                                        {section.src && (
                                            <div className="mt-4">
                                                <h4>Preview:</h4>
                                                <img
                                                    src={section.src}
                                                    alt={section.alt || "Selected"}
                                                    className="rounded border shadow"
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                                {/* Section content editing */}
                                {section.type === "quote" && (
                                    <div className="quote-editor">
                                        <textarea
                                            value={section.content}
                                            onChange={(e) =>
                                                updateSection(index, { ...section, content: e.target.value })
                                            }
                                            placeholder="Enter your quote here"
                                            className="border rounded px-4 py-2 w-full"
                                        ></textarea>
                                    </div>
                                )}

                                {section.type === "horizontal-rule" && (
                                    <div className="horizontal-rule-editor">
                                        <p className="text-gray-600">This section will render a horizontal line.</p>
                                    </div>
                                )}

                                {section.type === "line-break" && (
                                    <div className="line-break-editor">
                                        <p className="text-gray-600">This section will render a line break.</p>
                                    </div>
                                )}

                                {section.type === "section" && (
                                    <div className="section-editor">
                                        <input
                                            type="text"
                                            value={section.title}
                                            onChange={(e) =>
                                                updateSection(index, { ...section, title: e.target.value })
                                            }
                                            placeholder="Enter section title"
                                            className="border rounded px-4 py-2 w-full mb-2"
                                        />
                                        <textarea
                                            value={section.content}
                                            onChange={(e) =>
                                                updateSection(index, { ...section, content: e.target.value })
                                            }
                                            placeholder="Enter section content"
                                            className="border rounded px-4 py-2 w-full mb-2"
                                        ></textarea>
                                        <textarea
                                            value={section.additional}
                                            onChange={(e) =>
                                                updateSection(index, { ...section, additional: e.target.value })
                                            }
                                            placeholder="Enter additional content"
                                            className="border rounded px-4 py-2 w-full"
                                        ></textarea>
                                    </div>
                                )}

                                {section.type === "list" && (
                                    <div className="list-editor">
                                        <select
                                            value={section.listType}
                                            onChange={(e) =>
                                                updateSection(index, { ...section, listType: e.target.value })
                                            }
                                            className="border rounded px-2 py-1 mb-2"
                                        >
                                            <option value="unordered">Unordered</option>
                                            <option value="ordered">Ordered</option>
                                        </select>
                                        {section.items.map((item, itemIndex) => (
                                            <input
                                                key={itemIndex}
                                                type="text"
                                                value={item}
                                                onChange={(e) => {
                                                    const updatedItems = [...section.items];
                                                    updatedItems[itemIndex] = e.target.value;
                                                    updateSection(index, { ...section, items: updatedItems });
                                                }}
                                                placeholder={`Item ${itemIndex + 1}`}
                                                className="border rounded px-4 py-2 w-full mb-2"
                                            />
                                        ))}
                                        <button
                                            onClick={() =>
                                                updateSection(index, { ...section, items: [...section.items, ""] })
                                            }
                                            className="bg-blue-500 text-white px-4 py-2 rounded"
                                        >
                                            Add Item
                                        </button>
                                    </div>
                                )}

                                {section.type === "accordion" && (
                                    <div className="accordion-editor">
                                        {section.items.map((item, itemIndex) => (
                                            <div key={itemIndex} className="mb-4">
                                                <input
                                                    type="text"
                                                    value={item.title}
                                                    onChange={(e) => {
                                                        const updatedItems = [...section.items];
                                                        updatedItems[itemIndex].title = e.target.value;
                                                        updateSection(index, { ...section, items: updatedItems });
                                                    }}
                                                    placeholder="Accordion Title"
                                                    className="border rounded px-4 py-2 w-full mb-2"
                                                />
                                                <textarea
                                                    value={item.content}
                                                    onChange={(e) => {
                                                        const updatedItems = [...section.items];
                                                        updatedItems[itemIndex].content = e.target.value;
                                                        updateSection(index, { ...section, items: updatedItems });
                                                    }}
                                                    placeholder="Accordion Content"
                                                    className="border rounded px-4 py-2 w-full"
                                                ></textarea>
                                            </div>
                                        ))}
                                        <button
                                            onClick={() =>
                                                updateSection(index, {
                                                    ...section,
                                                    items: [...section.items, { title: "", content: "" }],
                                                })
                                            }
                                            className="bg-blue-500 text-white px-4 py-2 rounded"
                                        >
                                            Add Accordion Item
                                        </button>
                                    </div>
                                )}

                                {section.type === "card" && (
                                    <div className="card-editor">
                                        <input
                                            type="text"
                                            value={section.title}
                                            onChange={(e) =>
                                                updateSection(index, { ...section, title: e.target.value })
                                            }
                                            placeholder="Card Title"
                                            className="border rounded px-4 py-2 w-full mb-2"
                                        />
                                        <textarea
                                            value={section.body}
                                            onChange={(e) =>
                                                updateSection(index, { ...section, body: e.target.value })
                                            }
                                            placeholder="Card Body"
                                            className="border rounded px-4 py-2 w-full mb-2"
                                        ></textarea>
                                        <ImageUploader
                                            onUpload={(path) =>
                                                updateSection(index, {
                                                    ...section,
                                                    image: { ...section.image, src: path },
                                                })
                                            }
                                        />
                                        <ImageSelector
                                            onSelect={(path) =>
                                                updateSection(index, {
                                                    ...section,
                                                    image: { ...section.image, src: path },
                                                })
                                            }
                                        />
                                        {section.image.src && (
                                            <div className="mt-4">
                                                <h4>Image Preview:</h4>
                                                <img
                                                    src={section.image.src}
                                                    alt={section.image.alt || "Card Image"}
                                                    className="rounded border shadow"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {section.type === "audio" && (
                                    <div className="audio-editor">
                                        <input
                                            type="text"
                                            value={section.title}
                                            onChange={(e) =>
                                                updateSection(index, { ...section, title: e.target.value })
                                            }
                                            placeholder="Playlist Title"
                                            className="border rounded px-4 py-2 w-full mb-2"
                                        />
                                        {section.playlist.map((track, trackIndex) => (
                                            <div key={trackIndex} className="mb-4">
                                                <input
                                                    type="text"
                                                    value={track.title}
                                                    onChange={(e) => {
                                                        const updatedPlaylist = [...section.playlist];
                                                        updatedPlaylist[trackIndex].title = e.target.value;
                                                        updateSection(index, { ...section, playlist: updatedPlaylist });
                                                    }}
                                                    placeholder="Track Title"
                                                    className="border rounded px-4 py-2 w-full mb-2"
                                                />
                                                <input
                                                    type="text"
                                                    value={track.src}
                                                    onChange={(e) => {
                                                        const updatedPlaylist = [...section.playlist];
                                                        updatedPlaylist[trackIndex].src = e.target.value;
                                                        updateSection(index, { ...section, playlist: updatedPlaylist });
                                                    }}
                                                    placeholder="Track URL"
                                                    className="border rounded px-4 py-2 w-full"
                                                />
                                            </div>
                                        ))}
                                        <button
                                            onClick={() =>
                                                updateSection(index, {
                                                    ...section,
                                                    playlist: [...section.playlist, { title: "", src: "" }],
                                                })
                                            }
                                            className="bg-blue-500 text-white px-4 py-2 rounded"
                                        >
                                            Add Track
                                        </button>
                                    </div>
                                )}
                                {section.type === "videoPlaylist" && (
                                    <div className="video-playlist-editor">
                                        {section.videos.map((video, videoIndex) => (
                                            <div key={videoIndex} className="mb-4">
                                                <input
                                                    type="text"
                                                    value={video.title}
                                                    onChange={(e) => {
                                                        const updatedVideos = [...section.videos];
                                                        updatedVideos[videoIndex].title = e.target.value;
                                                        updateSection(index, { ...section, videos: updatedVideos });
                                                    }}
                                                    placeholder="Video Title"
                                                    className="border rounded px-4 py-2 w-full mb-2"
                                                />
                                                <input
                                                    type="text"
                                                    value={video.url}
                                                    onChange={(e) => {
                                                        const updatedVideos = [...section.videos];
                                                        updatedVideos[videoIndex].url = e.target.value;
                                                        updateSection(index, { ...section, videos: updatedVideos });
                                                    }}
                                                    placeholder="Video URL"
                                                    className="border rounded px-4 py-2 w-full"
                                                />
                                            </div>
                                        ))}
                                        <button
                                            onClick={() =>
                                                updateSection(index, {
                                                    ...section,
                                                    videos: [...section.videos, { title: "", url: "" }],
                                                })
                                            }
                                            className="bg-blue-500 text-white px-4 py-2 rounded"
                                        >
                                            Add Video
                                        </button>
                                    </div>
                                )}
                                {section.type === "iframe" && (
                                    <div className="iframe-editor">
                                        <input
                                            type="text"
                                            value={section.src}
                                            onChange={(e) =>
                                                updateSection(index, { ...section, src: e.target.value })
                                            }
                                            placeholder="Iframe Source URL"
                                            className="border rounded px-4 py-2 w-full mb-2"
                                        />
                                        <input
                                            type="text"
                                            value={section.className}
                                            onChange={(e) =>
                                                updateSection(index, { ...section, className: e.target.value })
                                            }
                                            placeholder="CSS Class"
                                            className="border rounded px-4 py-2 w-full mb-2"
                                        />
                                        <textarea
                                            value={JSON.stringify(section.style, null, 2)}
                                            onChange={(e) =>
                                                updateSection(index, { ...section, style: JSON.parse(e.target.value) })
                                            }
                                            placeholder="Custom Styles (JSON)"
                                            className="border rounded px-4 py-2 w-full"
                                        ></textarea>
                                    </div>
                                )}
                                <button
                                    onClick={() => removeSection(index)}
                                    className="bg-red-500 text-white px-4 py-2 rounded mt-4"
                                >
                                    Remove Section
                                </button>
                            </div>
                        )}
                    </div>
                ))}

                <div className="flex flex-wrap gap-4 mt-4">
                    <button
                        onClick={() => addSection("image")}
                        className="bg-blue-500 text-white px-4 py-2 rounded"
                    >
                        Add Image
                    </button>
                    <button onClick={() => addSection("text")} className="bg-blue-500 text-white px-4 py-2 rounded">
                        Add Text
                    </button>
                    <button onClick={() => addSection("quote")} className="bg-blue-500 text-white px-4 py-2 rounded">
                        Add Quote
                    </button>
                    <button
                        onClick={() => addSection("horizontal-rule")}
                        className="bg-blue-500 text-white px-4 py-2 rounded"
                    >
                        Add Horizontal Rule
                    </button>
                    <button onClick={() => addSection("list")} className="bg-blue-500 text-white px-4 py-2 rounded">
                        Add List
                    </button>
                    <button
                        onClick={() => addSection("accordion")}
                        className="bg-blue-500 text-white px-4 py-2 rounded"
                    >
                        Add Accordion
                    </button>
                    <button onClick={() => addSection("card")} className="bg-blue-500 text-white px-4 py-2 rounded">
                        Add Card
                    </button>
                    <button onClick={() => addSection("audio")} className="bg-blue-500 text-white px-4 py-2 rounded">
                        Add Audio
                    </button>

                    <button onClick={() => addSection("iframe")} className="bg-blue-500 text-white px-4 py-2 rounded">
                        Add Iframe
                    </button>
                    <button onClick={() => addSection("videoPlaylist")}
                            className="bg-blue-500 text-white px-4 py-2 rounded">
                        Add Video Playlist
                    </button>

                    <button onClick={() => addSection("paypal-donation")}
                            className="bg-blue-500 text-white px-4 py-2 rounded">
                        Add PayPal Donation
                    </button>
                    <button onClick={() => addSection("venmo-button")}
                            className="bg-blue-500 text-white px-4 py-2 rounded">
                        Add Venmo Button
                    </button>
                </div>
            </div>

            <div className="controls mt-8">
                <button
                    onClick={handleSave}
                    className="bg-green-500 text-white px-4 py-2 rounded"
                >
                    Save Page
                </button>
                <button
                    onClick={onCancel}
                    className="bg-gray-500 text-white px-4 py-2 rounded ml-4"
                >
                    Cancel
                </button>
            </div>

            <div className="preview mt-8">
                <h3 className="text-2xl font-bold mb-4">Page Preview</h3>
                <DynamicPage adminPageData={{ title: metadata.title, sections }} />
            </div>
        </div>
    );
};

export default PageComposer;